import { Box, Heading } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type SourcingEventV2Dto,
  type TextDocumentDto,
  useCreateSourcingEventV2TextDocumentMutation,
  useListTextDocumentsForSourcingEventV2Query,
  type ViewDocumentAsOwnerDto,
} from "../../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../../common/documents/CombinedDocuments";
import { IncompleteFileUpload, isIncompleteFileUpload, UploadCompletedStatus } from "../../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../../common/documents/UppyUploader/useUppyUploader";
import { StepNavigation } from "../../../../../common/StepNavigation";

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  refetch: () => void;
  isActive: boolean;
}

export const DocumentsStep = ({ eventId, event, previousStep, nextStep, nextStepLabel, refetch, isActive }: Props) => {
  const { t } = useTranslation();

  const { uppy, allFiles, documents, setDocuments, removeUpload, reloadEntity } = useUppyUploader({
    entityType: "SourcingEventV2",
    entityId: eventId,
    reloadEntity: refetch,
  });

  const [createTextDocument, { isLoading: isCreatingTextDocument }] = useCreateSourcingEventV2TextDocumentMutation();
  const { data: textDocumentRes, isLoading: isLoadingTextDocuments } = useListTextDocumentsForSourcingEventV2Query({
    eventId,
  });
  const [textDocuments, setTextDocuments] = useState<TextDocumentDto[]>([]);

  useEffect(() => {
    if (isActive) refetch();
  }, [isActive, refetch]);

  useEffect(() => {
    setDocuments(event.eventData.documents ?? []);
  }, [event.eventData.documents, setDocuments]);

  useEffect(() => {
    if (textDocumentRes) setTextDocuments(textDocumentRes.documents);
  }, [textDocumentRes]);

  const createTextDocumentForEvent = useCallback(
    () =>
      createTextDocument({
        createSourcingEventV2TextDocumentRequest: { eventId, name: "", text: "", tags: [] },
      }).unwrap(),
    [createTextDocument, eventId]
  );

  return (
    <Box pb="8" pt="10">
      <Heading as="h2" size="md" mb="6">
        {t("Documents")}
      </Heading>

      <CombinedDocuments
        uploadedDocuments={documents}
        setUploadedDocuments={setDocuments}
        textDocuments={textDocuments}
        setTextDocuments={setTextDocuments}
        createTextDocument={createTextDocumentForEvent}
        isCreatingTextDocument={isCreatingTextDocument}
        canEditTextDocuments
        isLoading={isLoadingTextDocuments}
        removeUploadedDocument={(document: ViewDocumentAsOwnerDto) =>
          removeUpload({
            status: UploadCompletedStatus.UploadCompleted,
            documentId: document.id,
            uploadId: document.fromUploadId,
            ...document,
          })
        }
        isTagsColumnVisible={false}
        isCategoryColumnVisible
        canEditCategory
        canDeleteDocuments
        canEditDocumentNames
        documentUploadConfig={{
          uppy,
          dropTargetId: `documents-step-${eventId}`,
          dropTargetHelperText: t("Drag and drop files here, or click to browse"),
          uploads: allFiles.filter(isIncompleteFileUpload) as IncompleteFileUpload[],
          removeUpload,
        }}
        refetchDocuments={() => { reloadEntity(); }}
      />

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
